import React from 'react';
import { Download, Eye, Loader2, Trash2, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { moduleApi, adminApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { generateInvoicePdfFromData } from './invoicePdfGenerator';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import { buildDocumentFilename } from '../../lib/documentFilename';
import { useConfirm } from '../ui/ConfirmProvider';
import { toCurrency } from './invoiceTypes';

/**
 * L'historique des factures (maquette 8a) — l'écran de référence du patron « liste-tableau » :
 * en-têtes de colonnes en petites capitales, montants en chiffres comparables, statuts en pastille
 * d'état, actions en fin de ligne.
 *
 * Les colonnes n'ont pas changé. Ce qui change : la mention « Jamais envoyée » là où la colonne
 * « Envoyé à » était vide, et l'état d'envoi affiché en ligne plutôt qu'en alert() du navigateur.
 */

interface InvoiceHistoryProps {
    refreshKey?: number;
    onOpenInvoice: (invoice: any) => void;
    /** Alimente le compteur de l'en-tête (« 12 factures · 1 impayée »). */
    onCountsChange?: (counts: { total: number; unpaid: number }) => void;
}

/** Le document PDF d'une facture enregistrée, avec le repli sur les réglages à jour. */
const buildPdfInputs = async (item: any) => {
    // mapFacture already parses these — use directly, no re-parse
    const payload = (item.payload_json && typeof item.payload_json === 'object')
        ? item.payload_json
        : (() => { try { return JSON.parse(item.payload_json || '{}'); } catch { return {}; } })();

    let snapshot = (item.billing_snapshot && typeof item.billing_snapshot === 'object')
        ? item.billing_snapshot
        : (() => { try { return JSON.parse(item.billing_snapshot || '{}'); } catch { return {}; } })();

    // Fallback : si le snapshot n'a pas de raison sociale, on recharge les réglages de facturation
    if (!snapshot?.company_name) {
        try {
            const fresh = await moduleApi.getBillingSettings();
            if (fresh?.company_name) snapshot = { ...fresh, ...snapshot };
        } catch { /* ignore */ }
    }

    // Logo en URL absolue, pour le rendu dans l'iframe
    if (snapshot?.logo_url) snapshot = { ...snapshot, logo_url: resolveLogoUrl(snapshot.logo_url) };

    return { payload, snapshot };
};

export const InvoiceHistory = ({ refreshKey = 0, onOpenInvoice, onCountsChange }: InvoiceHistoryProps) => {
    const { user } = useAuth();
    const confirmAction = useConfirm();
    const [items, setItems] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [deletingId, setDeletingId] = React.useState<string | null>(null);
    const [downloadingId, setDownloadingId] = React.useState<string | null>(null);
    const [resendingId, setResendingId] = React.useState<string | null>(null);
    const [resentId, setResentId] = React.useState<string | null>(null);
    // L'échec d'une action reste sur sa ligne, au lieu d'un alert() du navigateur qui ne dit pas
    // de quelle facture il parle (passation §6).
    const [rowError, setRowError] = React.useState<{ id: string; message: string } | null>(null);

    const isSuperAdminSession = Boolean(
        user?.type === 'admin' ||
        (user?.type === 'client' && user?.impersonatedBySuperAdmin)
    );

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const data = await moduleApi.getFactures();
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        load();
    }, [load, refreshKey]);

    React.useEffect(() => {
        onCountsChange?.({
            total: items.length,
            unpaid: items.filter((item) => Number(item.remaining_due ?? 0) > 0).length,
        });
    }, [items, onCountsChange]);

    const handleResend = React.useCallback(async (item: any) => {
        if (!item?.id || !item?.last_sent_email) return;
        setRowError(null);
        setResendingId(String(item.id));
        try {
            const { payload, snapshot } = await buildPdfInputs(item);

            const { pdf } = await generateInvoicePdfFromData({ invoice: item, payload, snapshot });
            const dataUri: string = pdf.output('datauristring');
            const pdfBase64 = dataUri.split(',')[1];

            await moduleApi.sendFactureEmail(String(item.id), {
                to: item.last_sent_email,
                pdfBase64,
                filename: `Facture_${item.invoice_number || item.id}.pdf`,
                invoicePayload: payload,
            });
            setResentId(String(item.id));
            setTimeout(() => setResentId((cur) => (cur === String(item.id) ? null : cur)), 3000);
            load();
        } catch (e: any) {
            console.error('Resend error:', e);
            setRowError({ id: String(item.id), message: e?.message || 'Impossible de renvoyer la facture.' });
        } finally {
            setResendingId(null);
        }
    }, [load]);

    const handleDownload = React.useCallback(async (item: any) => {
        setRowError(null);
        setDownloadingId(String(item.id));
        try {
            const { payload, snapshot } = await buildPdfInputs(item);
            const { pdf } = await generateInvoicePdfFromData({ invoice: item, payload, snapshot });
            pdf.save(buildDocumentFilename(snapshot?.company_name, 'FACTURE', item.invoice_number || item.id));
            adminApi.logAction({
                action: 'DOWNLOAD_FACTURE',
                category: 'METIER',
                severity: 'info',
                message: `Téléchargement du document : Facture ${item.invoice_number || item.id}`
            }).catch(() => {});
        } catch (e: any) {
            console.error('Download error:', e);
            setRowError({ id: String(item.id), message: e?.message || 'Impossible de générer le PDF.' });
        } finally {
            setDownloadingId(null);
        }
    }, []);

    // Suppression : la modale de confirmation reste, contrairement aux autres modules passés au
    // bandeau d'annulation (passation §6, cas irréversible). Une facture porte un numéro consommé,
    // elle a pu partir au client, et la comptabilité en dépend — mais la confirmation NOMME
    // maintenant ce qui va disparaître au lieu de demander un oui générique.
    const handleDelete = React.useCallback(async (invoice: any) => {
        if (!invoice?.id) return;

        const sentLine = invoice.last_sent_email
            ? <>Elle a été envoyée à <strong>{invoice.last_sent_email}</strong> : le client en garde une copie.</>
            : <>Elle n’a jamais été envoyée.</>;

        const confirmed = await confirmAction({
            title: 'Supprimer cette facture ?',
            description: (
                <>
                    <strong>{invoice.invoice_number || 'Facture sans numéro'}</strong>
                    {invoice.customer_name ? <> — {invoice.customer_name}</> : null}
                    {' · '}{toCurrency(Number(invoice.total_ttc || invoice.amount || 0))} TTC
                    <br />{sentLine}
                    <br />Le numéro reste consommé et la suppression est définitive.
                </>
            ),
            confirmLabel: 'Supprimer la facture',
        });
        if (!confirmed) return;

        setRowError(null);
        setDeletingId(String(invoice.id));
        try {
            await moduleApi.deleteFacture(String(invoice.id));
            setItems((current) => current.filter((entry) => String(entry.id) !== String(invoice.id)));
        } catch (e: any) {
            console.error(e);
            setRowError({ id: String(invoice.id), message: e?.message || 'Impossible de supprimer cette facture.' });
        } finally {
            setDeletingId(null);
        }
    }, [confirmAction]);

    const columns = ['N° Facture', 'Date', 'Client', 'Envoyé à', 'Montant TTC', 'Reste à payer', 'Actions'];

    return (
        <div className="bg-surface border border-border rounded-2xl overflow-hidden">
            <div className="overflow-x-auto scroll-slim">
                <table className="w-full text-left min-w-[900px]">
                    <thead>
                        <tr className="bg-surface-soft border-b border-border">
                            {columns.map((label, i) => (
                                <th
                                    key={label}
                                    className={`px-[18px] py-2.5 text-[10px] font-bold uppercase tracking-[.09em] text-content-muted whitespace-nowrap ${i >= 4 ? 'text-right' : ''}`}
                                >
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="px-[18px] py-10 text-center text-[13px] text-content-muted">
                                    <Loader2 size={15} className="inline animate-spin mr-2" /> Chargement…
                                </td>
                            </tr>
                        ) : items.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-[18px] py-10 text-center text-[13px] text-content-muted">Aucune facture enregistrée.</td>
                            </tr>
                        ) : items.map((item) => {
                            const id = String(item.id);
                            const due = Number(item.remaining_due ?? 0);
                            const error = rowError?.id === id ? rowError.message : null;
                            return (
                                <React.Fragment key={id}>
                                    <tr className="border-b border-border/60 last:border-0 hover:bg-surface-soft/60 transition-colors">
                                        <td className="px-[18px] py-2.5 font-mono text-[11.5px] font-medium text-content whitespace-nowrap">{item.invoice_number || '-'}</td>
                                        <td className="px-[18px] py-2.5 text-[12.5px] text-content-muted tabular-nums whitespace-nowrap">
                                            {item.due_date ? new Date(item.due_date).toLocaleDateString('fr-FR') : new Date(item.created_at).toLocaleDateString('fr-FR')}
                                        </td>
                                        <td className="px-[18px] py-2.5 text-[12.5px] font-semibold text-content max-w-[220px] truncate">{item.customer_name || '-'}</td>
                                        <td className="px-[18px] py-2.5 max-w-[240px]">
                                            {item.last_sent_email ? (
                                                <span className="block text-[12px] text-content-muted truncate">{item.last_sent_email}</span>
                                            ) : (
                                                <span className="inline-flex items-center h-[22px] px-2 rounded-full text-[10.5px] font-bold whitespace-nowrap shrink-0" style={{ background: 'var(--st-warn-bg)', color: 'var(--st-warn-fg)' }}>Jamais envoyée</span>
                                            )}
                                        </td>
                                        <td className="px-[18px] py-2.5 text-[12.5px] font-semibold text-content text-right tabular-nums whitespace-nowrap">{toCurrency(Number(item.total_ttc || item.amount || 0))}</td>
                                        <td className={`px-[18px] py-2.5 text-[12.5px] font-semibold text-right tabular-nums whitespace-nowrap ${due > 0 ? 'text-state-danger' : 'text-content-muted'}`}>{toCurrency(due)}</td>
                                        <td className="px-[18px] py-2.5">
                                            <div className="flex items-center justify-end gap-0.5">
                                                <button
                                                    type="button"
                                                    title="Aperçu"
                                                    aria-label={`Aperçu de la facture ${item.invoice_number || ''}`}
                                                    onClick={() => onOpenInvoice(item)}
                                                    className="w-[30px] h-[30px] flex items-center justify-center rounded-lg text-content-muted hover:bg-surface-soft hover:text-content transition-colors shrink-0"
                                                >
                                                    <Eye size={15} />
                                                </button>
                                                <button
                                                    type="button"
                                                    title="Télécharger le PDF"
                                                    aria-label={`Télécharger la facture ${item.invoice_number || ''}`}
                                                    disabled={downloadingId === id}
                                                    onClick={() => handleDownload(item)}
                                                    className="w-[30px] h-[30px] flex items-center justify-center rounded-lg text-content-muted hover:bg-surface-soft hover:text-content transition-colors disabled:opacity-60 shrink-0"
                                                >
                                                    {downloadingId === id ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                                                </button>
                                                {item.last_sent_email && (
                                                    <button
                                                        type="button"
                                                        disabled={resendingId === id}
                                                        onClick={() => handleResend(item)}
                                                        className="h-[30px] px-3 ml-1 inline-flex items-center gap-1.5 rounded-full border border-border bg-surface text-[11.5px] font-semibold text-content hover:border-content transition-colors disabled:opacity-60 whitespace-nowrap shrink-0"
                                                    >
                                                        {resendingId === id
                                                            ? <><Loader2 size={13} className="animate-spin" /> Envoi…</>
                                                            : resentId === id
                                                                ? <><CheckCircle2 size={13} /> Envoyée</>
                                                                : <><Send size={13} /> Renvoyer</>
                                                        }
                                                    </button>
                                                )}
                                                {isSuperAdminSession && (
                                                    <button
                                                        type="button"
                                                        title="Supprimer"
                                                        aria-label={`Supprimer la facture ${item.invoice_number || ''}`}
                                                        onClick={() => handleDelete(item)}
                                                        disabled={deletingId === id}
                                                        className="w-[30px] h-[30px] ml-1 flex items-center justify-center rounded-lg text-[#8f5450] hover:bg-state-danger-soft hover:text-state-danger transition-colors disabled:opacity-60 shrink-0"
                                                    >
                                                        {deletingId === id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {error && (
                                        <tr className="border-b border-border/60">
                                            <td colSpan={7} className="px-[18px] pb-2.5">
                                                <span className="inline-flex items-center gap-1.5 text-[12px] text-state-danger">
                                                    <AlertCircle size={13} className="shrink-0" /> {error}
                                                </span>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
